import { cookieStorage } from '../utils/cookieStorage';
import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {fetchRoutePermissions, RoutePerm} from '../services/permissionService';
import {useAuth} from './AuthContext';
import {useLocation} from 'react-router-dom';
import {ROUTES} from '../routes/routePaths';
import {checkRouteAccess} from '../services/accessDecisionService';

type Change = {permission?: RoutePerm; deletedId?: number};
interface PermissionsContextType {
    permissions: Record<string,string[]>;
    disabledPaths: string[];
    loading: boolean;
}
const PermissionsContext = createContext<PermissionsContextType>({permissions:{}, disabledPaths:[], loading:true});

export const PermissionsProvider: React.FC<{children:React.ReactNode}> = ({children}) => {
    const {isAuthenticated, user} = useAuth();
    const {pathname} = useLocation();
    const administrator = user?.role === 'Administrador';
    const [rules, setRules] = useState<RoutePerm[]>([]);
    const [loading, setLoading] = useState(true);
    const [revision, setRevision] = useState(0);
    const generation = useRef(0);
    const owner = useRef<string | null>(null);
    const resolvedPaths = useRef<Set<string>>(new Set());
    const adminRef = useRef(administrator);
    adminRef.current = administrator;
    const authenticated = useRef(isAuthenticated);
    authenticated.current = isAuthenticated;

    useEffect(() => {
        let cookieRevision = cookieStorage.getItem('comarca_route_permissions_revision');
        const apply = (change?: Change) => {
            if (!authenticated.current) return;
            if (!adminRef.current) {setRevision(value => value + 1); return;}
            if (!change?.permission && change?.deletedId === undefined) {setRevision(value => value + 1); return;}
            generation.current += 1;
            setRules(current => change.permission
                ? [...current.filter(rule => rule.id !== change.permission!.id), change.permission]
                : current.filter(rule => rule.id !== change.deletedId));
        };
        const changed = (event: Event) => {
            const detail = (event as CustomEvent<Change>).detail;
            apply(detail);
            cookieRevision = cookieStorage.getItem('comarca_route_permissions_revision');
        };
        const focused = () => setRevision(value => value + 1);
        const timer = window.setInterval(() => {
            const current = cookieStorage.getItem('comarca_route_permissions_revision');
            if (current !== cookieRevision) {cookieRevision = current; setRevision(value => value + 1);}
        }, 500);
        window.addEventListener('route-permissions-changed', changed);
        window.addEventListener('focus', focused);
        return () => {
            window.clearInterval(timer);
            window.removeEventListener('route-permissions-changed', changed);
            window.removeEventListener('focus', focused);
        };
    }, []);

    useEffect(() => {
        let active = true;
        const version = generation.current += 1;
        if (!isAuthenticated) {
            owner.current = null;
            resolvedPaths.current.clear();
            setRules([]);
            setLoading(false);
            return;
        }
        const identity = `${user?.id}:${user?.role}`;
        const identityChanged = owner.current !== identity;
        if (identityChanged) {
            owner.current = identity;
            resolvedPaths.current.clear();
            setRules([]);
        }
        // Recheck on focus without unmounting a page already authorized for
        // this account. New accounts and unchecked paths still wait for a decision.
        if (identityChanged || (!administrator && !resolvedPaths.current.has(pathname))) {
            setLoading(true);
        }
        const paths = Array.from(new Set([...Object.values(ROUTES), pathname]));
        const request = administrator ? fetchRoutePermissions() : checkRouteAccess(paths).then(allowed =>
            paths.map((path, index) => ({id: index, path, activo: true,
                allowed_roles: allowed[index] && user?.role ? [user.role] : []})));
        request.then(list => {
            if (!active || generation.current !== version) return;
            resolvedPaths.current = new Set(paths);
            setRules(list);
            setLoading(false);
        }).catch(error => {
            if (!active || generation.current !== version) return;
            resolvedPaths.current.clear();
            setRules([]);
            setLoading(false);
            /* Browser diagnostics omitted. */
        });
        return () => {active = false;};
    }, [isAuthenticated, user?.id, user?.role, administrator, pathname, revision]);

    const permissions = useMemo(() => Object.fromEntries(rules.map(rule => [rule.path, rule.activo === false ? [] : rule.allowed_roles])), [rules]);
    const disabledPaths = useMemo(() => rules.filter(rule => rule.activo === false).map(rule => rule.path), [rules]);
    return <PermissionsContext.Provider value={{permissions, disabledPaths, loading}}>{children}</PermissionsContext.Provider>;
};
export const usePermissions = () => useContext(PermissionsContext);
